import { Component, computed, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { Message } from 'primeng/message';
import { Tag } from 'primeng/tag';
import { Fluid } from 'primeng/fluid';
import { Tooltip } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { SiteTemplateService } from '@/app/services/site-template.service';
import { BrandKit, BrandKitService } from '@/app/services/brand-kit.service';
import { TemplateInfo } from '@/app/components/template-info';
import { CategoryType, RealmConfig, SiteTemplate } from '@/lib-backend';

interface TemplateOption {
    label: string;
    value: string;
    published: boolean;
}

/**
 * Realm → SiteTemplate assignment. A realm serves its site only while it points
 * at a template that has a published version; clearing the pointer (or pointing
 * at a version-less template) is a hard cutover to 404. Global-admin only.
 */
@Component({
    selector: 'app-website-info-site-template',
    standalone: true,
    imports: [FormsModule, Button, Select, Message, Tag, Fluid, Tooltip, TemplateInfo],
    templateUrl: './site-template-tab.html',
})
export class SiteTemplateTab implements OnInit {
    protected readonly service = inject(SiteTemplateService);
    private readonly brandKitService = inject(BrandKitService);
    private readonly messageService = inject(MessageService);

    readonly realm = input.required<RealmConfig>();
    /** Emits the updated RealmConfig returned by the assignment call. */
    readonly realmUpdated = output<RealmConfig>();

    /** Selected pointer; null means "not assigned" (offline). */
    protected readonly selectedId = signal<string | null>(null);
    protected readonly saving = signal(false);
    protected readonly error = signal<string | null>(null);

    /** Brand-kit preview of the currently selected template (style + assets). */
    protected readonly brandKit = signal<BrandKit | null>(null);
    protected readonly brandKitLoading = signal(false);

    /** Templates serving this realm's category type — assignment is by type. */
    protected readonly templatesForType = computed<SiteTemplate[]>(() => {
        const type = this.realm().CategorySpecific.type;
        return this.service.templates().filter(t => t.CategoryType === type);
    });

    protected readonly options = computed<TemplateOption[]>(() =>
        this.templatesForType().map(t => ({
            value: t.SiteTemplateId,
            published: t.CurrentVersion !== null,
            label: t.CurrentVersion ? `${t.Name} — v${t.CurrentVersion}` : `${t.Name} — not published`,
        }))
    );

    protected readonly currentTemplate = computed<SiteTemplate | null>(() => {
        const id = this.realm().SiteTemplateId;
        return id ? this.service.templates().find(t => t.SiteTemplateId === id) ?? null : null;
    });

    /** The template currently picked in the dropdown (may differ from saved). */
    protected readonly selectedTemplate = computed<SiteTemplate | null>(() => {
        const id = this.selectedId();
        return id ? this.service.templates().find(t => t.SiteTemplateId === id) ?? null : null;
    });

    protected readonly dirty = computed(() => this.selectedId() !== this.realm().SiteTemplateId);

    /** Selecting a version-less template, or clearing the pointer, takes the site offline. */
    protected readonly willGoOffline = computed(() => {
        const id = this.selectedId();
        if (!id) return true;
        return this.selectedTemplate()?.CurrentVersion == null;
    });

    private readonly typeLabel = computed(() =>
        this.realm().CategorySpecific.type === CategoryType.SkillBasedGames
            ? 'Skill-based games'
            : 'Lucky-based games'
    );

    /** Empty-state copy for the Template Info panel, contextual to the selection. */
    protected readonly templateInfoEmpty = computed(() =>
        this.selectedId()
            ? 'This template has no brand kit — it ships no meta/brand-kit.json, or has no published version yet.'
            : 'Select a template to preview its info.'
    );

    constructor() {
        // Re-seed the dropdown whenever the realm (or its saved pointer) changes.
        effect(() => {
            const realm = this.realm();
            this.selectedId.set(realm.SiteTemplateId);
            this.error.set(null);
        });

        // Resolve the brand-kit preview for the selected template (or clear it).
        effect(() => {
            const id = this.selectedId();
            this.brandKit.set(null);
            if (!id) return;
            this.brandKitLoading.set(true);
            void this.brandKitService.resolve(id).then((kit) => {
                if (this.selectedId() === id) {
                    this.brandKit.set(kit);
                    this.brandKitLoading.set(false);
                }
            });
        });
    }

    ngOnInit(): void {
        if (this.service.templates().length === 0) {
            this.service.loadTemplates();
        }
    }

    protected categoryTypeLabel(): string {
        return this.typeLabel();
    }

    protected reset(): void {
        this.selectedId.set(this.realm().SiteTemplateId);
        this.error.set(null);
    }

    protected async apply(): Promise<void> {
        const realm = this.realm();
        this.error.set(null);
        this.saving.set(true);
        try {
            const { result, config } = await this.service.setRealmTemplate({
                RealmId: realm.RealmId,
                SiteTemplateId: this.selectedId(),
            });
            switch (result) {
                case 'Ok':
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Site template updated',
                        detail: this.selectedId() ? 'Template assigned.' : 'Template cleared — site is now offline.',
                    });
                    if (config) {
                        this.realmUpdated.emit(config);
                    }
                    break;
                case 'CategoryTypeMismatch':
                    this.error.set('That template serves a different category type. Reload and pick another.');
                    break;
                case 'SiteTemplateNotFound':
                    this.error.set('Template no longer exists. Reloading the list.');
                    await this.service.loadTemplates();
                    break;
                case 'RealmNotFound':
                    this.error.set('This website no longer exists.');
                    break;
                default:
                    this.error.set('Failed to update the site template.');
            }
        } finally {
            this.saving.set(false);
        }
    }
}
